import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const report = ({ added, skipped_duplicates: s }) =>
  toast.success(`${added} client${added === 1 ? "" : "s"} added${s ? ` · ${s} already existed` : ""}`);

export default function ImportClientsDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setText("");
  }, [open]);

  const finish = (data) => { report(data); onClose(); onDone(); };

  const run = async (fn) => {
    setBusy(true);
    try {
      finish((await fn()).data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const paste = (e) => {
    e.preventDefault();
    const names = text.split("\n").map((n) => n.trim()).filter(Boolean);
    if (names.length) run(() => api.post("/clients/bulk", { names }));
  };

  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post("/clients/import", fd));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-md" data-testid="import-clients-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">Import clients</DialogTitle>
          <DialogDescription>Client names only. Names that already exist are skipped.</DialogDescription>
        </DialogHeader>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={upload} data-testid="import-clients-file-input" />
        <button type="button" disabled={busy} onClick={() => fileRef.current.click()} data-testid="import-clients-file-button"
          className="w-full rounded-2xl border-2 border-dashed border-[#103080]/20 bg-white py-6 text-sm text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200 inline-flex flex-col items-center gap-2">
          <Upload size={20} />
          <span className="font-semibold">Upload spreadsheet (.xlsx or .csv)</span>
          <span className="text-xs text-[#0F172A]/50">Names in the first column. A header row is fine.</span>
        </button>
        <form onSubmit={paste} className="space-y-3">
          <Label>Or paste names, one per line</Label>
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={"Bristol Bakes Ltd\nBath Joinery Co"}
            className="rounded-xl bg-white border-[#103080]/20" data-testid="import-clients-textarea" />
          <div className="flex justify-end">
            <button type="submit" disabled={busy || !text.trim()} data-testid="import-clients-submit"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200 disabled:opacity-50">
              Add clients
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
